import * as XLSX from 'xlsx-js-style';
import { unzipSync, zipSync, strFromU8, strToU8 } from 'fflate';

// The monthly attendance register, styled like Atten-format.xlsx: one row per employee, one column per day.
//   A-J  (blank) | Date of Joining | Leave upto (last month) | Absent on (this month) | Total Leave | Present | LOP | Total days | Name | Team
//   K..  one column per date (dd/mm) with the weekday under it, holding a colour-coded status code
// followed by a legend of the codes.
const FIXED = ['', 'DATE OF JOINING', 'LEAVE UPTO\r\n', 'ABSENT ON', 'TOTAL LEAVE', 'PRESENT', 'LOP', 'TOTAL DAYS', 'NAME', 'TEAM'];
const SUB = ['', '', 'LAST MONTH', 'THIS MONTH', '', '', '', 'IN A MONTH', '', ''];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// code -> [fill colour, description]
export const REGISTER_CODES = [
    ['P', 'B4C6E7', 'Present'],
    ['A', 'FF0000', 'Absent'],
    ['1HF-P', 'FFC000', '1st Half Leave'],
    ['P-2HF', 'FFFF00', '2nd Half Leave'],
    ['CO-A', '7B7B7B', 'Comp Off Leave'],
    ['HOLIDAY', null, 'Holiday'],
    ['WO', 'C65911', 'Week-Off'],
    ['-', 'FFFFFF', 'Resigned / New Joiner'],
    ['SL', 'FFD966', 'Sick Leave'],
    ['PH', 'D5A6BD', 'Public Holiday'],
    ['CL', 'F4B183', 'Casual Leave'],
    ['WFH', 'A9D18E', 'Work from Home'],
    ['LOP', 'EA9999', 'Loss of Pay'],
    ['HD', 'FFE699', 'Half Day'],
    ['LEAVE', 'B7DEE8', 'Approved Leave'],
];
const FILL = Object.fromEntries(REGISTER_CODES.map(([c, f]) => [c, f]));
const WHITE_TEXT = new Set(['CO-A', 'WO']);

const side = (rgb) => ({ style: 'thin', color: { rgb } });
const box = (rgb = '000000') => ({ top: side(rgb), bottom: side(rgb), left: side(rgb), right: side(rgb) });
const mid = { horizontal: 'center', vertical: 'center' };
const font = (o) => ({ name: 'Calibri', color: { rgb: '000000' }, ...o });
const solid = (rgb) => ({ patternType: 'solid', fgColor: { rgb } });

const parseDmy = (s) => { const [d, m, y] = String(s || '').split('-').map(Number); return new Date(y, m - 1, d); };
const excelSerial = (d) => Math.round((Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) - Date.UTC(1899, 11, 30)) / 86400000);

// One day's cell from GetAttendanceRegister -> the register code. The server already decides P / A / HALF / LEAVE /
// HOLIDAY / "-" / blank; a LEAVE cell is shortened by its leave type name.
export const dayCode = (day) => {
    const c = String(day?.code ?? '').trim();
    if (c === 'HALF') return 'HD';
    if (c === 'LEAVE') {
        const t = String(day.leaveTypeName || '').toLowerCase();
        if (/sick/.test(t)) return 'SL';
        if (/casual/.test(t)) return 'CL';
        if (/comp/.test(t)) return 'CO-A';
        if (/loss|lop/.test(t)) return 'LOP';
        return 'LEAVE';
    }
    return c;
};

const codeStyle = (code) => {
    const fill = FILL[code];
    if (code === 'HOLIDAY') return { alignment: mid, border: box(), font: font({ bold: true, sz: 10, color: { rgb: '00B0F0' } }) };
    return {
        alignment: mid, border: box(),
        font: font({ bold: true, sz: code === 'A' ? 10 : 11, color: { rgb: WHITE_TEXT.has(code) ? 'FFFFFF' : '000000' } }),
        ...(fill && { fill: solid(fill) }),
    };
};

// staff: rows of GetAttendanceRegister, with joined as a Date|null: { name, department, joined, leaveUptoLastMonth,
// absentDays, leaveDays, presentDays, halfDays, lopDays, days: [{ code, leaveTypeName }] }, dateHeaders: ['dd-mm-yyyy', ...]
export function exportAttendanceRegister({ staff, dateHeaders, sheetName, fileName }) {
    const dates = dateHeaders.map(parseDmy);
    const n = dates.length;

    const aoa = [
        [...FIXED, ...dates.map(excelSerial)],
        [...SUB, ...dates.map((d) => DAYS[d.getDay()])],
    ];
    const rows = staff.map((p, i) => {
        const codes = dates.map((d, k) => dayCode(p.days?.[k]));
        const present = (Number(p.presentDays) || 0) + (Number(p.halfDays) || 0) / 2;
        return { codes, row: [i + 1, p.joined ? excelSerial(p.joined) : '', Number(p.leaveUptoLastMonth) || 0, Number(p.absentDays) || 0, Number(p.leaveDays) || 0, present, Number(p.lopDays) > 0 ? Number(p.lopDays) : '−', n, p.name, p.department || '', ...codes] };
    });
    rows.forEach((r) => aoa.push(r.row));

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const R0 = 2; // first data row (0-based)

    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let R = 0; R <= range.e.r; R++) {
        for (let C = 0; C <= range.e.c; C++) {
            const addr = XLSX.utils.encode_cell({ r: R, c: C });
            const cell = ws[addr] || (ws[addr] = { t: 's', v: '' });
            if (R < 2) {
                cell.s = { fill: solid('002060'), border: box(), alignment: { ...mid, wrapText: C === 2 || C === 7 }, font: font({ bold: true, sz: C === 0 ? 13 : 12, color: { rgb: 'FFFFFF' } }) };
                if (C >= 10 && R === 0) cell.z = 'dd/mm';
                continue;
            }
            if (C >= 10) { cell.s = codeStyle(cell.v); continue; }
            const text = C >= 8; // name and team: lighter fill, Times New Roman, wrapped
            cell.s = {
                alignment: text ? { horizontal: 'center', wrapText: true } : mid,
                border: box(C === 9 ? 'CCCCCC' : '000000'),
                font: text ? { name: 'Times New Roman', bold: true, sz: 10, color: { rgb: '000000' } } : font({ bold: true, sz: 12 }),
                fill: solid(text ? 'DDEBF7' : 'DEEAF6'),
            };
            if (C === 1) cell.z = 'dd/mm/yyyy';
        }
    }
    ws['!merges'] = FIXED.map((_, c) => ({ s: { r: 0, c }, e: { r: 1, c } })).filter((m) => !SUB[m.s.c]);
    ws['!cols'] = [7.6, 15.8, 14.6, 19.4, 13.5, 11.1, 12, 14.1, 25.6, 28.5, ...dates.map(() => 8.6)].map((wch) => ({ wch }));
    ws['!rows'] = [{ hpt: 15.5 }, { hpt: 15.5 }, ...rows.map(() => ({ hpt: 19.5 }))];

    // legend under the table: code (coloured) beside its meaning
    const top = R0 + rows.length + 3;
    REGISTER_CODES.forEach(([code, , text], i) => {
        ws[`C${top + i}`] = { t: 's', v: code, s: codeStyle(code) };
        ws[`D${top + i}`] = { t: 's', v: text, s: { alignment: mid, border: box(), font: font({ bold: true, sz: 10 }) } };
    });
    ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: Math.max(range.e.r, top + REGISTER_CODES.length), c: range.e.c } });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, String(sheetName || 'Attendance').slice(0, 31));

    // The style writer has no frozen-pane option, so the panes are added to the sheet XML afterwards:
    // columns A-I and the two header rows.
    const files = unzipSync(new Uint8Array(XLSX.write(wb, { bookType: 'xlsx', type: 'array' })));
    const freeze = (path, x, y) => {
        if (!files[path]) return;
        const pane = `<pane${x ? ` xSplit="${x}"` : ''} ySplit="${y}" topLeftCell="${XLSX.utils.encode_cell({ r: y, c: x })}" activePane="${x ? 'bottomRight' : 'bottomLeft'}" state="frozen"/>`;
        files[path] = strToU8(strFromU8(files[path]).replace(/<sheetView([^>]*?)\/>/, `<sheetView$1>${pane}</sheetView>`));
    };
    freeze('xl/worksheets/sheet1.xml', 9, 2);
    const blob = new Blob([zipSync(files)], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = fileName;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}
